import React, { useCallback } from 'react';
import {
  PartialNullable,
  Participant,
  ProfessionalSituation,
  useCurrentParticipant,
  useUpdateParticipant,
} from '@app/api';
import { useToast, SquareButton, SubmitButton } from '@app/core';
import { Theme } from '@app/theme';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { OnboardingButtons } from './OnboardingButtons';
import { OnboardingLayout } from './OnboardingLayout';
import { steps, orderedSteps } from './steps';

const useStyles = createUseStyles((theme: Theme) => ({
  form: {
    height: '100%',
  },

  button: {
    [theme.media.mobile]: {
      width: '100%',
    },
  },
}));
export const OnboardingSteps = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { step: step = 'Demographic' } = useParams<{ step: string }>();
  const goToStep = useCallback(
    (dest: string) => navigate(`/onboarding/steps/${dest}`),
    [navigate],
  );

  const { data: participant } = useCurrentParticipant();
  const { mutateAsync: updateParticipant } = useUpdateParticipant(
    participant?.id,
  );

  const stepIndex = orderedSteps.indexOf(step);

  const next = useCallback(() => {
    if (stepIndex < orderedSteps.length - 1) {
      goToStep(orderedSteps[stepIndex + 1]);
    } else {
      navigate('/onboarding/studies');
    }
  }, [stepIndex, goToStep]);

  const prev = useCallback(() => {
    if (stepIndex > 0) {
      goToStep(orderedSteps[stepIndex - 1]);
    } else {
      navigate('/onboarding');
    }
  }, [stepIndex, goToStep, navigate]);

  const submit = useCallback(
    async (
      values: PartialNullable<Participant>,
      { setValues }: FormikHelpers<Participant>,
    ) => {
      try {
        let newValues = values;
        if (
          values.professionalSituation === ProfessionalSituation.STUDENT &&
          values.jobTitle === null
        ) {
          newValues = {
            ...values,
            jobTitle: null,
            companySize: null,
            companyType: null,
            hierarchicalLevel: null,
            industries: null,
          };
        }
        const p = await updateParticipant(newValues);
        setValues(p);
        toast.saved();
        next();
      } catch (e) {
        toast.exception(e);
        throw e;
      }
    },
    [updateParticipant, next, toast],
  );

  const stepNode = steps[step];

  if (!participant) {
    return null;
  }
  if (!stepNode) {
    return <Navigate to="/onboarding" />;
  }
  return (
    <Formik onSubmit={submit} initialValues={participant}>
      <Form className={classes.form}>
        <OnboardingLayout isLoading={!participant} currStep={stepIndex}>
          {stepNode}
          <OnboardingButtons>
            <SquareButton
              icon="ri-arrow-left-line"
              onClick={prev}
              color="ink"
            />
            <SubmitButton small className={classes.button} withNextArrow>
              {t('onboarding.continue')}
            </SubmitButton>
          </OnboardingButtons>
        </OnboardingLayout>
      </Form>
    </Formik>
  );
};
